import './ContactList.scss';
import React, { useMemo, useCallback } from 'react';
import { Warning, Star } from '@phosphor-icons/react';
import {
  siGmail,
  siGooglecalendar,
  siGoogle,
  siSlack,
  siX,
  siReddit,
} from 'simple-icons';
import { useContactsAppStore } from '../../store/contactsAppStore';
import { ContactEntity, getWarmthLevel, buildTierLabels, buildTierCadenceDays } from '../../types/entity';
import { formatRelativeTime } from '../../utils/time';

interface ContactListProps {
  windowId: string;
  spaceId: string;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// Maps a name to one of 6 avatar color slots deterministically.
function getAvatarColorIndex(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return hash % 6;
}

function isStaleContact(contact: ContactEntity, cadenceMap: Record<string, number | null>): boolean {
  const cadence = cadenceMap[contact.skeleton.tier];
  if (!cadence || !contact.skeleton.lastInteraction?.at) return false;
  return (Date.now() - new Date(contact.skeleton.lastInteraction.at).getTime()) / 86400000 >= cadence;
}

const SOURCE_PIP: Record<string, { gradient: string; path: string }> = {
  gmail:           { gradient: 'linear-gradient(160deg,#ff6d57,#EA4335)', path: siGmail.path },
  calendar:        { gradient: 'linear-gradient(160deg,#5fa8fb,#1a73e8)', path: siGooglecalendar.path },
  google_contacts: { gradient: 'linear-gradient(160deg,#5bc87a,#34A853)', path: siGoogle.path },
  slack:           { gradient: 'linear-gradient(160deg,#ed3278,#611f69)', path: siSlack.path },
  twitter:         { gradient: 'linear-gradient(160deg,#333,#000)', path: siX.path },
  reddit:          { gradient: 'linear-gradient(160deg,#ff7043,#FF4500)', path: siReddit.path },
};

function getSourcePip(contact: ContactEntity): { gradient: string; path: string } | null {
  const sources = contact.skeleton.sources;
  if (!sources?.length) return null;
  const order = ['gmail', 'calendar', 'google_contacts', 'slack', 'reddit', 'twitter'];
  for (const src of order) {
    if (sources.includes(src) && SOURCE_PIP[src]) return SOURCE_PIP[src];
  }
  return null;
}

const TIER_ACCENT: Record<string, string> = {
  inner_circle: '#f59e0b',
  tier1:        'var(--theme-primary)',
  tier2:        'var(--theme-success, #22c55e)',
  tier3:        'var(--theme-text-muted)',
  dormant:      'var(--theme-border-primary)',
};

function HighlightMatch({ text, query }: { text: string; query?: string }): React.ReactElement {
  if (!query?.trim() || !text) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="contact-list__highlight">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}

function ContactRow({
  contact,
  isSelected,
  onClick,
  onToggleFavorite,
  snippet,
  searchQuery,
  cadenceMap,
  tierLabels,
}: {
  contact: ContactEntity;
  isSelected: boolean;
  onClick: () => void;
  onToggleFavorite: (e: React.MouseEvent) => void;
  snippet?: string;
  searchQuery?: string;
  cadenceMap: Record<string, number | null>;
  tierLabels: Record<string, string>;
}) {
  const sk = contact.skeleton;
  const lastAt = sk.lastInteraction?.at;
  const nextAt = sk.nextReachOutAt;
  const warmthLevel = sk.warmthScore !== undefined ? getWarmthLevel(sk.warmthScore) : null;
  const stale = isStaleContact(contact, cadenceMap);
  const pip = getSourcePip(contact);
  const isFavorite = sk.tier === 'inner_circle';
  const tierAccent = TIER_ACCENT[sk.tier] ?? 'var(--theme-border-primary)';

  const subLabel = sk.email || sk.primaryChannelContext || null;

  return (
    <div
      className={`contact-list__item${isSelected ? ' contact-list__item--selected' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
      style={{ '--tier-accent': tierAccent } as React.CSSProperties}
    >
      <div className="contact-list__avatar-wrap">
        <div
          className="contact-list__avatar"
          data-color={sk.avatarUrl ? undefined : getAvatarColorIndex(contact.name)}
        >
          {sk.avatarUrl ? (
            <img src={sk.avatarUrl} alt={contact.name} className="contact-list__avatar-img" />
          ) : (
            <span className="contact-list__avatar-initials">{getInitials(contact.name)}</span>
          )}
        </div>
        {pip && (
          <span className="contact-list__avatar-pip" style={{ background: pip.gradient }}>
            <svg viewBox="0 0 24 24" width={8} height={8} fill="#fff" aria-hidden>
              <path d={pip.path} />
            </svg>
          </span>
        )}
      </div>
      <div className="contact-list__info">
        <div className="contact-list__row1">
          <span className="contact-list__name">
            <HighlightMatch text={contact.name} query={searchQuery} />
            {stale && (
              <span title="Overdue for engagement">
                <Warning
                  size={12}
                  weight="fill"
                  className="contact-list__stale-icon"
                />
              </span>
            )}
          </span>
          <div className="contact-list__badges">
            {warmthLevel && (
              <span
                className={`contact-list__warmth contact-list__warmth--${warmthLevel}`}
                title={`Warmth: ${warmthLevel}`}
              />
            )}
            <span className={`contact-list__tier contact-list__tier--${sk.tier}`}>
              {tierLabels[sk.tier] || sk.tier}
            </span>
          </div>
        </div>
        {(sk.company || sk.title) && (
          <span className="contact-list__company">
            {sk.title ? `${sk.title}${sk.company ? ' · ' : ''}` : ''}
            {sk.company && <HighlightMatch text={sk.company} query={searchQuery} />}
          </span>
        )}
        {snippet ? (
          <span className="contact-list__snippet" title={snippet}>{snippet}</span>
        ) : subLabel ? (
          <span className="contact-list__sublabel">
            <HighlightMatch text={subLabel} query={searchQuery} />
          </span>
        ) : (
          <div className="contact-list__row3">
            {lastAt && (
              <span className="contact-list__last">Last: {formatRelativeTime(lastAt)}</span>
            )}
            {nextAt && (
              <span className="contact-list__next">Next: {formatRelativeTime(nextAt)}</span>
            )}
          </div>
        )}
      </div>
      <button
        className={`contact-list__star${isFavorite ? ' contact-list__star--active' : ''}`}
        onClick={onToggleFavorite}
        title={isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
        aria-label={isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
      >
        <Star size={14} weight={isFavorite ? 'fill' : 'regular'} />
      </button>
    </div>
  );
}

export const ContactList: React.FC<ContactListProps> = ({ windowId, spaceId }) => {
  const store = useContactsAppStore(windowId, spaceId);
  const selectedContactId = store((s) => s.selectedContactId);
  const selectContact     = store((s) => s.selectContact);
  const updateContact     = store((s) => s.updateContact);
  const getFilteredContacts = store((s) => s.getFilteredContacts);
  const rawContacts       = store((s) => s.contacts);
  const currentView       = store((s) => s.currentView);
  const filter            = store((s) => s.filter);
  const searchSnippets    = store((s) => s.searchSnippets);
  const searchResults     = store((s) => s.searchResults);
  const tierConfig        = store((s) => s.tierConfig);
  const tierLabels        = useMemo(() => buildTierLabels(tierConfig), [tierConfig]);
  const tierCadence       = useMemo(() => buildTierCadenceDays(tierConfig), [tierConfig]);

  const isSearchMode = filter.search.length > 0;
  const searchQuery  = isSearchMode ? filter.search : undefined;

  const contacts = useMemo(
    () => getFilteredContacts(),
    [rawContacts, currentView, filter, searchResults, getFilteredContacts]
  );

  const handleToggleFavorite = useCallback((contact: ContactEntity) => (e: React.MouseEvent) => {
    e.stopPropagation();
    const newTier = contact.skeleton.tier === 'inner_circle' ? 'tier1' : 'inner_circle';
    updateContact(contact._id, { tier: newTier });
  }, [updateContact]);

  if (contacts.length === 0) {
    return (
      <div className="contact-list contact-list--empty">
        {filter.search ? (
          <>
            <p>No contacts match &ldquo;{filter.search}&rdquo;</p>
            <span>Try a shorter query or check the spelling</span>
          </>
        ) : (
          <>
            <p>No contacts yet</p>
            <span>Add one or use Discover to find them from Gmail</span>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="contact-list">
      {contacts.map((contact) => (
        <ContactRow
          key={contact._id}
          contact={contact}
          isSelected={contact._id === selectedContactId}
          onClick={() => selectContact(contact._id)}
          onToggleFavorite={handleToggleFavorite(contact)}
          snippet={searchSnippets[contact._id]}
          searchQuery={searchQuery}
          cadenceMap={tierCadence}
          tierLabels={tierLabels}
        />
      ))}
    </div>
  );
};
